import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faBoxOpen, faReceipt } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import SupervisorHistoryButton from '../common/SupervisorHistoryButton';
import { useTranslation } from 'react-i18next';
import CoilLineChips from '../coils/CoilLineChips';

const InfoRow = ({ label, value, isBig = false }) => {
    // Don't render if value is empty/null/undefined
    if (value === null || value === undefined || value === '') return null;

    return (
        <div className="flex flex-col">
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: UI_COLORS.TEXT.MUTED }}>
                {label}
            </span>
            <span className={`font-medium mt-0.5 leading-tight ${isBig ? 'text-base' : 'text-[13px]'}`} style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {value}
            </span>
        </div>
    );
};

const formatDate = (val, t) => {
    const emDash = t('common.symbols.emDash');
    if (!val) return emDash;
    try {
        return new Date(val).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
        return emDash;
    }
};

const PurchaseChallanViewModal = ({ isOpen, onClose, challan, loading = false }) => {
    const { t } = useTranslation();
    if (!isOpen) return null;

    const strings = t('purchaseChallan.view', { returnObjects: true });
    const emDash = t('common.symbols.emDash');

    const challanNumber = challan && challan.challan_number ? challan.challan_number : strings.header.fallbackTitle;
    const supplierName = challan && challan.supplier_name ? challan.supplier_name : emDash;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
            <style>{`.pc-view-modal{ -ms-overflow-style: none; scrollbar-width: none; } .pc-view-modal::-webkit-scrollbar{ display: none; }`}</style>
            <div className="w-full max-w-5xl max-h-[90vh] flex flex-col rounded-2xl bg-white shadow-2xl" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
                {/* Header */}
                <div className="flex items-center justify-between border-b px-6 py-5 flex-shrink-0" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <div>
                        <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
                            {strings.header.sectionTitle}
                        </p>
                        <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                            {challanNumber}
                        </h2>
                        <p className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{supplierName}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-lg p-2 transition-all duration-200 hover:scale-105"
                        style={{
                            color: UI_COLORS.TEXT.MUTED,
                            backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
                        }}
                    >
                        <FontAwesomeIcon icon={faTimes} className="text-lg" />
                    </button>
                </div>

                <div className="pc-view-modal flex-1 overflow-y-auto px-6 py-6 space-y-6">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <div className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent" style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5) }}></div>
                            <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{strings.loading}</p>
                        </div>
                    ) : !challan ? (
                        <div className="rounded-xl border px-6 py-16 text-center" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                            <FontAwesomeIcon icon={faReceipt} className="text-4xl opacity-60" style={{ color: UI_COLORS.TEXT.MUTED }} />
                            <h3 className="mt-4 text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{strings.empty.title}</h3>
                            <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{strings.empty.message}</p>
                        </div>
                    ) : (
                        <>

                            {/* Main Info Grid */}
                            <div className="grid grid-cols-2 gap-x-3 gap-y-3 md:grid-cols-4 p-3 md:p-4 rounded-xl border bg-gray-50/30 mb-6" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                                <InfoRow label={strings.fields.challanNumber} value={challan.challan_number} />
                                <InfoRow label={strings.fields.actualQuantity} value={challan.actual_quantity} />
                                <InfoRow 
                                    label={strings.fields.remark} 
                                    value={(challan.po_remark || challan.remark) ? (
                                        <span className="block px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}>
                                            {challan.po_remark || challan.remark}
                                        </span>
                                    ) : null} 
                                />
                                <div className="hidden md:block"></div>
                                <InfoRow label={strings.fields.poNumber} value={challan.po_number} />
                                <InfoRow label={strings.fields.supplierName} value={challan.supplier_name} />

                                <InfoRow label={strings.fields.actualQuantity || 'Actual Qty'} value={Math.ceil(parseFloat(challan.total_quantity || challan.actual_quantity || 0))} />
                                <InfoRow label={strings.fields.subtotal || 'Total Value'} value={challan.total_value ? `${t('common.symbols.currency')}${Number(challan.total_value).toFixed(2)}` : (challan.subtotal ? `${t('common.symbols.currency')}${Number(challan.subtotal).toFixed(2)}` : null)} />
                                <InfoRow label={strings.fields.challanDate} value={formatDate(challan.challan_date, t)} />
                                <InfoRow label={strings.fields.date} value={formatDate(challan.date, t)} />

                                <InfoRow label={t('purchaseAdvice.view.fields.adviceNumber')} value={challan.advice_number} />
                                <InfoRow label={t('purchaseAdvice.view.fields.date')} value={formatDate(challan.advice_date, t)} />
                                <InfoRow label={strings.fields.supplierInvoiceNumber} value={challan.supplier_invoice_number} />
                                <InfoRow label={strings.fields.supplierInvoiceDate} value={formatDate(challan.supplier_invoice_date, t)} />

                                <InfoRow label={t('purchaseOrders.view.fields.warehouseName')} value={challan.warehouse_name} />
                                <SupervisorHistoryButton label={t('orders.view.fields.supervisorName')} supervisorName={challan.supervisor_name} supervisorHistory={challan.supervisor_history} />
                                <InfoRow label={strings.fields.vehicleNumber} value={challan.vehicle_number} />
                                <InfoRow label={t('common.formFields.labels.driverContact')} value={challan.driver_contact} />

                                <InfoRow label={strings.fields.emptyTruckWeight} value={challan.empty_truck_weight} />
                                <InfoRow label={strings.fields.finalTruckWeight || strings.fields.loadedTruckWeight} value={challan.loaded_truck_weight || challan.final_truck_weight} />
                                <InfoRow label={strings.fields.truckWeight} value={challan.truck_weight} />
                                <InfoRow label={t('purchaseOrders.view.fields.paymentTerms')} value={challan.payment_terms} />

                                <InfoRow label={t('purchaseOrders.view.fields.totalItems')} value={challan.total_items} />
                                <InfoRow label={t('purchaseAdvice.view.fields.receivedQty')} value={Math.ceil(parseFloat(challan.total_received_quantity || 0))} />
                                <InfoRow label={strings.fields.remark} value={challan.po_remark || challan.remark} />
                                <InfoRow label="Assigned by" value={challan.assigned_by_name} />
                                <InfoRow label="Purchase Advice Created By" value={challan.pa_created_by_name} />
                                <InfoRow label="Purchase Order Created By" value={challan.po_created_by_name} />
                                <InfoRow label="Challan Generated By" value={challan.challan_generated_by_name} />
                            </div>

                            {/* Items List - Tabular */}
                            <div className="mt-8">
                                <h3 className="text-sm font-semibold uppercase tracking-wide mb-4 flex items-center gap-2" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                    <FontAwesomeIcon icon={faBoxOpen} className="text-xs" />
                                    {strings.fields.items}
                                </h3>

                                <div className="rounded-xl border overflow-hidden" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left border-collapse whitespace-nowrap">
                                            <thead>
                                                <tr className="border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.1) }}>
                                                    <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">#</th>
                                                    <th className="px-4 py-4 text-xs font-bold uppercase tracking-wider text-gray-500">{strings.fields.item_product}</th>
                                                    <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">Pieces</th>
                                                    <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">{strings.fields.item_qty}</th>
                                                    <th className="px-4 py-4 text-right text-xs font-bold uppercase tracking-wider text-gray-500">{strings.fields.item_price}</th>
                                                    <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">Actual Quantity</th>
                                                    <th className="px-4 py-4 text-right text-xs font-bold uppercase tracking-wider text-gray-500">{strings.fields.item_amount}</th>
                                                    <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">{strings.fields.item_remark}</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y" style={{ divideColor: UI_COLORS.BORDERS.LIGHT }}>
                                                {Array.isArray(challan.items) && challan.items.length > 0 ? (
                                                    challan.items.map((it, idx) => (
                                                        <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                                                            <td className="px-4 py-4 text-center text-xs font-medium text-gray-400 align-top">
                                                                {idx + 1}
                                                            </td>
                                                            <td className="px-4 py-4 align-top space-y-1">
                                                                <p className="text-base font-bold max-w-xs text-wrap" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                                    {it.size_name || it.product_name || emDash}
                                                                </p>
                                                                <div className="flex flex-wrap gap-2 items-center max-w-xs text-wrap mt-1">
                                                                    {it.product_name && it.size_name && (
                                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-100">
                                                                            {it.product_name}
                                                                        </span>
                                                                    )}
                                                                    {it.hsn_code && (
                                                                        <span className="text-xs font-medium text-gray-500">
                                                                            HSN: {it.hsn_code}
                                                                        </span>
                                                                    )}
                                                                    {it.gst_percentage !== undefined && (
                                                                        <span className="text-xs font-medium text-gray-500">
                                                                            GST: {it.gst_percentage}%
                                                                        </span>
                                                                    )}
                                                                </div>
                                                              <CoilLineChips item={it} />
                                                            </td>
                                                            {/* Pieces */}
                                                            <td className="px-4 py-4 text-center align-top">
                                                                <p className="text-lg font-bold text-blue-600">
                                                                    {it.quantity_in_pieces || it.actual_pieces || emDash}
                                                                </p>
                                                            </td>
                                                            {/* Order Quantity */}
                                                            <td className="px-4 py-4 text-center align-top">
                                                                <p className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                                    {it.quantity ? `${Math.ceil(parseFloat(it.quantity))} ${it.unit || ''}` : emDash}
                                                                </p>
                                                            </td>
                                                            {/* Price */}
                                                            <td className="px-4 py-4 text-right align-top">
                                                                <p className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                                    {it.price != null ? `${t('common.symbols.currency')}${it.price}` : emDash}
                                                                </p>
                                                            </td>
                                                            {/* Actual Quantity — carry the line unit so a pcs line
                                                                reads "N pcs" (and a kg line "N kg"), matching Order Qty. */}
                                                            <td className="px-4 py-4 text-center align-top">
                                                                <p className="text-sm font-semibold text-green-600">
                                                                    {it.actual_quantity !== undefined ? `${Math.ceil(parseFloat(it.actual_quantity || 0))} ${it.unit || ''}` : emDash}
                                                                </p>
                                                            </td>
                                                            {/* Amount */}
                                                            <td className="px-4 py-4 text-right align-top">
                                                                <p className="text-sm font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                                    {it.amount ? `${t('common.symbols.currency')}${Number(it.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : emDash}
                                                                </p>
                                                            </td>
                                                            {/* Remark */}
                                                            <td className="px-4 py-4 text-center align-top min-w-[160px] max-w-[200px]">
                                                                {it.remark ? (
                                                                    <span className="block px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200 text-left" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}>
                                                                        {it.remark}
                                                                    </span>
                                                                ) : emDash}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="8" className="px-4 py-12 text-center">
                                                            <p className="text-sm text-gray-400">{strings.messages.no_items_available}</p>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 border-t px-6 py-4 flex-shrink-0" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <button
                        onClick={onClose}
                        className="rounded-xl px-5 py-3 font-semibold transition-all duration-200 hover:scale-105"
                        style={{
                            backgroundColor: 'transparent',
                            color: UI_COLORS.TEXT.MUTED,
                            border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}`,
                        }}
                    >
                        {strings.buttons.close}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PurchaseChallanViewModal;
